import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #animationVersion = 0;
  #focusContainer;
  #resizeObserver = new ResizeObserver(() => this.#onBreakpointChange());

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    this.#resizeObserver.observe(this.parentElement ?? this);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    ++this.#animationVersion;
    if (this.isOpen) this.#resetDrawer();
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    this.#resizeObserver.disconnect();
  }

  #onBreakpointChange = () => {
    requestAnimationFrame(() => {
      if (!this.isConnected || !this.isOpen || this.getClientRects().length) return;

      ++this.#animationVersion;
      this.#resetDrawer();
    });
  };

  #resetDrawer() {
    reset(this.refs.details);
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
    if (this.#focusContainer) removeTrapFocus(this.#focusContainer);
    this.#focusContainer = undefined;
    if (!document.querySelector('details[scroll-lock][open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  #trapDrawerFocus(details, target) {
    const panel = details.querySelector(':scope > .menu-drawer, :scope > .menu-drawer__submenu') ?? details;
    trapFocus(panel, { visibleOnly: true });
    this.#focusContainer = panel;
    const focusTarget = target ?? panel.querySelector('button:enabled, a[href], summary');
    if (focusTarget?.getClientRects().length && getComputedStyle(focusTarget).visibility !== 'hidden') {
      focusTarget.focus({ preventScroll: true });
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    const details = this.classList.contains('header-drawer--jp-grouped') && isMobileBreakpoint()
      ? this.refs.details
      : this.#getDetailsElement(event);
    this.#close(details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary || (details !== this.refs.details && !this.isOpen)) return;

    const version = ++this.#animationVersion;
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (version !== this.#animationVersion || !this.isConnected || !this.isOpen || !details.open) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        if (version === this.#animationVersion && this.isConnected && this.isOpen && details.open) {
          this.#trapDrawerFocus(details);
        }
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const version = ++this.#animationVersion;
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (version !== this.#animationVersion || !this.isConnected) return;
        reset(details);
        if (details === this.refs.details) {
          this.#resetDrawer();
          if (summary.getClientRects().length && getComputedStyle(summary).visibility !== 'hidden') {
            summary.focus({ preventScroll: true });
          }
        } else {
          const parent = details.parentElement?.closest('details') ?? this.refs.details;
          this.#trapDrawerFocus(parent, summary);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
